/**
 * Canlı akış aboneliği — `EventSource` ile SSE.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * "CANLI" GÖSTERGESİ GERÇEKTEN CANLIYKEN YANAR.
 *
 * Bağlantı düştüğünde ekranda duran veri BİR ANDA eskimeye başlar ama
 * görünüşü değişmez. Gösterge olmasaydı kullanıcı üç saat önceki bir tank
 * seviyesine bakıp "şu an böyle" diye karar verirdi — bu görev dizisinin
 * baştan beri kaçındığı yanlışın en pahalı hâli.
 *
 * Üç hâl ayrı: BAĞLI (canlı) · BAĞLANIYOR (tarayıcı kendi deniyor) · KAPALI
 * (akış yok; veri yalnız elle tazelenince güncellenir).
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * ─── OLAY GELİNCE YENİDEN SORGU ATILMIYOR ───────────────────────────────────
 * Gelen olay veriyi TAŞIYOR; önbellek doğrudan güncelleniyor. `invalidate`
 * çağırmak her olayda tam liste sorgusu üretirdi — 15 saniyede bir, açık her
 * sekmede.
 */
import { useEffect, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import type { VeriDurumu } from '@gasco/shared';

import { SESSION_KEY } from '../auth/session.ts';
import { OTURUM_SONA_ERDI_KEY } from '../auth/expiry.ts';
import type { OkumaListesi, OkumaSahasi } from './okuma.ts';

export type AkisDurumu = 'bagli' | 'baglaniyor' | 'kapali';

/** Sunucudan gelen kısmi satır — yalnız değişen alanlar. */
interface AkisSatiri {
  readonly site_id: number;
  readonly data_state: VeriDurumu;
  readonly last_ok_at: string | null;
  readonly consecutive_failures: number;
  readonly tanks: readonly {
    readonly tank_id: number;
    readonly level_pct: number | null;
    readonly pressure_bar: number | null;
    readonly read_at: string | null;
  }[];
}

interface AkisOlayi {
  readonly sites: readonly AkisSatiri[];
  readonly now: string;
}

/**
 * Gelen satırı önbellekteki sahaya UYGULAR.
 *
 * Akış yalnız değişen alanları taşıyor; ad, adres kodu, tank sayısı gibi
 * yapılandırma alanları REST'ten gelen hâliyle kalıyor. Satırı komple
 * değiştirmek o alanları silerdi.
 */
function uygula(mevcut: OkumaSahasi, satir: AkisSatiri): OkumaSahasi {
  const tanklar = mevcut.tanks.map((tank) => {
    const yeni = satir.tanks.find((t) => t.tank_id === tank.tank_id);
    return yeni === undefined
      ? tank
      : {
          ...tank,
          level_pct: yeni.level_pct,
          pressure_bar: yeni.pressure_bar,
          read_at: yeni.read_at,
        };
  });

  return {
    ...mevcut,
    data_state: satir.data_state,
    last_ok_at: satir.last_ok_at,
    consecutive_failures: satir.consecutive_failures,
    tanks: tanklar,
  };
}

export function useCanliOkumalar(etkin = true): AkisDurumu {
  const qc = useQueryClient();
  const [durum, setDurum] = useState<AkisDurumu>(etkin ? 'baglaniyor' : 'kapali');
  const kaynakRef = useRef<EventSource | null>(null);

  useEffect(() => {
    if (!etkin) {
      setDurum('kapali');
      return;
    }
    /*
     * `EventSource` tarayıcıda yoksa (eski cihaz, bazı gömülü tarayıcılar)
     * sessizce çökmek yerine "kapalı" diyoruz: ekran elle tazelemeye düşer
     * ve göstergesi bunu SÖYLER.
     */
    if (typeof EventSource === 'undefined') {
      setDurum('kapali');
      return;
    }

    const kaynak = new EventSource('/api/stream/readings', { withCredentials: true });
    kaynakRef.current = kaynak;

    const guncelle = (olay: MessageEvent<string>, tamListe: boolean): void => {
      const veri = JSON.parse(olay.data) as AkisOlayi;
      qc.setQueryData<OkumaListesi>(['okumalar', { q: null, sessiz: false }], (eski) => {
        if (eski === undefined) return eski;
        const yeniSahalar = eski.sites.map((saha) => {
          const satir = veri.sites.find((s) => s.site_id === saha.site_id);
          return satir === undefined ? saha : uygula(saha, satir);
        });
        /*
         * `now` da güncelleniyor: yaş hesabı SUNUCU saatine göre yapılıyor
         * (Görev 4) ve akış devam ederken damga eskimemeli — yoksa "2 dk
         * önce" yazan bir kart yarım saat sonra da öyle yazardı.
         */
        return { sites: yeniSahalar, now: veri.now };
      });
      void tamListe;
    };

    kaynak.addEventListener('snapshot', (o) => {
      setDurum('bagli');
      guncelle(o as MessageEvent<string>, true);
    });
    kaynak.addEventListener('update', (o) => {
      setDurum('bagli');
      guncelle(o as MessageEvent<string>, false);
    });

    /*
     * ─── OTURUM AKIŞIN ORTASINDA DÜŞERSE (S37'nin akış tarafı) ───────────────
     * `EventSource` hataları `istek()` katmanından geçmiyor; küresel 401
     * yakalayıcı bu bağlantıyı göremez. Sunucu özel bir olayla haber veriyor,
     * burada oturum durumu `null`'a çekiliyor ve `RequireSession` girişe
     * yönlendiriyor — tek fark, yolun bu kez akıştan gelmesi.
     */
    kaynak.addEventListener('oturum-bitti', () => {
      kaynak.close();
      setDurum('kapali');
      const oturum = qc.getQueryData(SESSION_KEY);
      if (oturum) {
        qc.setQueryData(OTURUM_SONA_ERDI_KEY, true);
        qc.setQueryData(SESSION_KEY, null);
      }
    });

    kaynak.addEventListener('open', () => {
      setDurum('bagli');
    });
    kaynak.addEventListener('error', () => {
      /*
       * Tarayıcı yeniden bağlanmayı kendi yapıyor; kapalı olup olmadığını
       * `readyState` söylüyor. "Bağlanıyor" ile "kapalı" ayrı, çünkü
       * birincisinde veri birazdan gelecek, ikincisinde hiç gelmeyecek.
       */
      setDurum(kaynak.readyState === EventSource.CLOSED ? 'kapali' : 'baglaniyor');
    });

    return () => {
      kaynak.close();
      kaynakRef.current = null;
    };
  }, [etkin, qc]);

  return durum;
}

export const AKIS_METNI: Record<AkisDurumu, { readonly etiket: string; readonly ipucu: string }> = {
  bagli: { etiket: 'Canlı', ipucu: 'Yeni ölçümler ekrana kendiliğinden düşüyor.' },
  baglaniyor: { etiket: 'Bağlanıyor…', ipucu: 'Canlı bağlantı kuruluyor.' },
  kapali: {
    etiket: 'Canlı değil',
    // Kullanıcı ne yapacağını bilmeli: veri eskiyor, tazeleme elle.
    ipucu: 'Canlı bağlantı yok — veriler yalnız sayfayı tazeleyince güncellenir.',
  },
};
